import { Form } from '@unform/web';
import { CompactSearch, CompactSelect } from 'components/Shared';
import { FindMany, SelectOption } from 'contracts/Common';
import { throttle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as S from './styles';

type SearchField = 'id' | 'document' | 'name' | 'city' | 'state';

export interface FindWarehouses extends FindMany {
  searchField?: SearchField;
}

const searchFieldOptions: SelectOption[] = [
  { label: 'ID', value: 'id' },
  { label: 'Documento', value: 'document' },
  { label: 'Nome', value: 'name' },
  { label: 'Cidade', value: 'city' },
  { label: 'Estado', value: 'state' },
];

interface Props {
  onFilter?: (query: FindWarehouses) => void;
  currentFilter: FindWarehouses;
  delay?: number;
  searchFields?: SearchField[];
}

export const WarehouseFilters: React.FC<Props> = ({
  delay = 1000,
  currentFilter,
  onFilter,
  searchFields,
}) => {
  const [filters, setFilters] = useState<FindWarehouses>({
    ...currentFilter,
    dirty: false,
  });

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setFilters((state) => ({
        ...state,
        dirty: true,
        search: e.target.value,
      }));
    },
    []
  );

  const handleSearchField = useCallback((option: SelectOption | null): void => {
    if (!option) return;
    setFilters((state) => ({
      ...state,
      dirty: true,
      searchField: option.value as SearchField,
    }));
  }, []);

  const availableSearchFieldOptions = useMemo(
    () =>
      searchFields
        ? searchFields
            .map((field) =>
              searchFieldOptions.find((option) => option.value === field)
            )
            .filter((option): option is SelectOption => Boolean(option))
        : searchFieldOptions,
    [searchFields]
  );

  const selectedSearchField = useMemo(
    () =>
      availableSearchFieldOptions.find(
        (option) => option.value === filters.searchField
      ) ||
      availableSearchFieldOptions.find((option) => option.value === 'name') ||
      availableSearchFieldOptions[0],
    [availableSearchFieldOptions, filters.searchField]
  );

  // throttled methods

  const onSearch = useMemo(
    () => throttle(handleSearch, delay),
    [delay, handleSearch]
  );

  useEffect(() => {
    if (filters.dirty && onFilter) {
      onFilter(filters);
    }
  }, [filters, onFilter]);

  return (
    <S.Container>
      <Form onSubmit={() => {}}>
        <CompactSearch
          onChange={onSearch}
          defaultValue={filters?.search}
          placeholder="Buscar"
          name="search"
          aria-label="Buscar"
        />
        <CompactSelect
          name="searchField"
          inputId="warehouses-search-field"
          aria-label="Buscar por"
          options={availableSearchFieldOptions}
          value={selectedSearchField}
          onChange={handleSearchField}
          isSearchable={false}
        />
      </Form>
    </S.Container>
  );
};
